/**
 * Sticky client-side experiment assignment.
 *
 * Buckets are persisted so a user keeps the same variant across remounts.
 * This matters more than it looks: the gifting flow remounts on Plaid OAuth
 * returns, Stripe Financial Connections returns and post-login redirects, and
 * a re-roll would both corrupt the measurement and drop the user on a
 * different screen than the one they left.
 */

const STORAGE_KEY = 'gg_experiments';

export const EXPERIMENTS = {
    GIFTING_AMOUNT_FIRST: 'gifting_amount_first',
    SIGNUP_CONTACT_FIRST: 'signup_contact_first',
};

export const VARIANTS = {
    CONTROL: 'control',
    TREATMENT: 'treatment',
};

/**
 * Share of traffic sent to treatment, per experiment.
 *
 * 1 = 100% treatment (pre/post against historical data).
 * `?gg_exp=<name>:control` still forces the old flow for QA.
 */
const ROLLOUT = {
    [EXPERIMENTS.GIFTING_AMOUNT_FIRST]: 1,
    [EXPERIMENTS.SIGNUP_CONTACT_FIRST]: 1,
};

function readAssignments() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        // Private mode / storage disabled — fall back to per-session assignment.
        return {};
    }
}

function writeAssignments(assignments) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
    } catch (e) {
        // Assignment still works for this page view, it just won't persist.
    }
}

/**
 * QA / support override: ?gg_exp=gifting_amount_first:treatment
 * Persisted like a normal assignment so it survives the rest of the flow.
 */
function readUrlOverride(experimentName) {
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('gg_exp');
        if (!raw) return null;

        const match = raw.split(',')
            .map(pair => pair.trim().split(':'))
            .find(([name]) => name === experimentName);

        if (!match || match.length < 2) return null;
        const variant = match[1];
        return variant === VARIANTS.TREATMENT || variant === VARIANTS.CONTROL ? variant : null;
    } catch (e) {
        return null;
    }
}

/**
 * Resolve the variant for an experiment, assigning (and persisting) on first call.
 * Safe to call from a constructor — it never triggers a render or a network call.
 */
export function getVariant(experimentName) {
    const assignments = readAssignments();

    const override = readUrlOverride(experimentName);
    if (override && assignments[experimentName] !== override) {
        assignments[experimentName] = override;
        writeAssignments(assignments);
        return override;
    }

    const existing = assignments[experimentName];
    if (existing === VARIANTS.CONTROL || existing === VARIANTS.TREATMENT) {
        return existing;
    }

    const rollout = ROLLOUT[experimentName] != null ? ROLLOUT[experimentName] : 0;
    const variant = Math.random() < rollout ? VARIANTS.TREATMENT : VARIANTS.CONTROL;

    assignments[experimentName] = variant;
    writeAssignments(assignments);
    return variant;
}

export function isTreatment(experimentName) {
    return getVariant(experimentName) === VARIANTS.TREATMENT;
}

/** All resolved assignments, for stamping onto Amplitude as user properties. */
export function getAllVariants() {
    return Object.values(EXPERIMENTS).reduce((acc, name) => {
        acc[name] = getVariant(name);
        return acc;
    }, {});
}
